import { Injectable } from '@angular/core';
import { HttpParams, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { of } from 'rxjs';
import { environment } from '../environments/environment';
import { flatMap, map, tap, toArray, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Account } from './models/account';
import { ApiDevice, ConvertApiDevice, Device } from './models/device';
import { Headset, HeadsetCalibration } from './models/headset';

@Injectable()
export class AccountService {
  url: string = environment.accounts_api;

  href: string = environment.accounts_api;
  // accounts: any[] = [];
  accounts$ = new BehaviorSubject<Account[]>([]);
  devices$ = new BehaviorSubject<any[]>([]);

  constructor(private http: HttpClient, private auth: AuthService) {
    console.log('CONSTRUCTING ACCOUNT SERVICE');
    this.searchAccount('').subscribe((data) => {
      this.accounts$.next(data);
    });

    this.findDevicesByImei('').subscribe((data: any) => {
      this.devices$.next(data);
    });
  }

  login(email: string, password: string): Observable<LoginResult> {
    const requestUrl = `${this.href}/account/v2/login`;
    return this.http.post<AccountsAPIResult<LoginResult>>(
      requestUrl,
      { email, password },
    ).pipe(
      map(x => x.data),
    );
  }

  searchAccount(q: string, limit?: number): Observable<Account[]> {
    const requestUrl = `${this.href}/account/v1/search`;
    const headers = new HttpHeaders({
      Authorization: 'token ' + this.auth.getAccountsToken(),
    });
    return this.http.post<AccountsAPIResult<AccountSearchResult>>(
      requestUrl,
      {
        search: q,
        pagination: {
            per_page: limit || null,
            page: 1
        }
      },
      { headers: headers },
    ).pipe(
      map(d => d.data.collection)
    );
  }
  // searchAccount(q: string): Observable<any> {
  //   const requestUrl = `${this.href}/account/accounts?s=${q}`;
  //   const headers = new HttpHeaders({
  //     Authorization: 'token ' + this.auth.getAccountsToken(),
  //   });
  //   return this.http.get<any>(requestUrl, { headers: headers }).pipe(
  //     map(d => d.data.object)
  //   );
  // }

  byId(id: number): Observable<any> {
    return this.accounts$.pipe(
      // tap(x => console.log(x)),
      map(x => x.find((d) => d.id === id)),
    );
  }

  deviceById(id: number): Observable<any> {
    return this.devices$.pipe(
      // tap(x => console.log(x)),
      map(x => {
        return x.find((d) => Number(d.id) === id);
      }));
  }

  linkKioskToAccount(b: { account_id: number, kiosk_id: number }) {
    const url = `${this.url}/v3/management/kiosk/account/link`;
    const body = b;

    return this.http.post(url, body);
  }

  createAccountWithoutPassword(a: { email: string, first_name: string, last_name: string }) {
    const url = `${this.url}/account/register_without_password`;
    const body = a;

    return this.http.post(url, body);
  }

  findDevicesByImei(imei: string) {
    const requestUrl = `${environment.hardware_api}/v1/find_device?imei=${imei}`;

    const headers = new HttpHeaders({
      Authorization: 'jwt_token ' + this.auth.getHardwareToken(),
    });

    return this.http.get<DeviceSearchResponse>(requestUrl, { headers: headers }).pipe(
      flatMap((d) => {
        console.log(d);
        return d.devices || [];
      }),
      map((d: any) => {
        let jsonData: any = {};
        try {
          jsonData = JSON.parse(d.data);
        } catch (e) {
          // console.log('could not parse', d, e);
        }
        return Object.assign(
          {}, d,
          {
            model: jsonData.model
          });
      }),
      toArray()
    );
  }

  searchHeadsets(serial: string): Observable<HeadsetSearchResponse> {
    // /v1/headset/find/d5e6530595
    const requestUrl = `${environment.hardware_api}/v1/headset/find/${serial}`;

    const headers = new HttpHeaders({
      Authorization: 'jwt_token ' + this.auth.getHardwareToken(),
    });

    return this.http.get<HeadsetSearchResponse>(requestUrl, { headers: headers });
  }

  findDevicesForAccount(accountID: number): Observable<Device[]> {
    const requestUrl = `${environment.hardware_api}/v1/account/${accountID}/devices`;

    const headers = new HttpHeaders({
      Authorization: 'jwt_token ' + this.auth.getHardwareToken(),
    });

    return this.http.get<AccountDevicesResponse>(requestUrl, { headers: headers }).pipe(
      flatMap((rsp) => {
        console.log(rsp);
        // return d.devices || [];
        if (!rsp.devices) {
          return of([]);
        }
        return of(rsp.devices.map(ConvertApiDevice));
      }),
    );
  }

  findHeadsetsForAccount(accountID: number): Observable<Headset[]> {
    const requestUrl = `${environment.hardware_api}/v1/account/${accountID}/hardware_items`;

    const headers = new HttpHeaders({
      Authorization: 'jwt_token ' + this.auth.getHardwareToken(),
    });

    return this.http.get<AccountHeadsetsResponse>(requestUrl, { headers: headers }).pipe(
      map(x => x.hardware_items),
    );
  }

  getHeadsetByID(id: number): Observable<HeadsetSearchResponse> {
    const requestUrl = `${environment.hardware_api}/v1/hardware_item/${id}`;

    const headers = new HttpHeaders({
      Authorization: 'jwt_token ' + this.auth.getHardwareToken(),
    });

    return this.http.get<HeadsetSearchResponse>(requestUrl, { headers: headers });
  }

  unlinkAccountFromHeadset(id: number): Observable<void> {
    const requestUrl = `${environment.hardware_api}/v1/hardware_item/${id}/unlink_account`;

    const headers = new HttpHeaders({
      Authorization: 'jwt_token ' + this.auth.getHardwareToken(),
    });

    return this.http.post<any>(requestUrl, {}, { headers: headers });
  }

  unlinkAccountFromDevice(id: number): Observable<void> {
    const requestUrl = `${environment.hardware_api}/v1/device/${id}/unlink_account`;

    const headers = new HttpHeaders({
      Authorization: 'jwt_token ' + this.auth.getHardwareToken(),
    });

    return this.http.post<any>(requestUrl, {}, { headers: headers });
  }

  getCertificateURL(calibrationID: number): Observable<string> {
    const requestUrl = `${environment.hardware_api}/v1/headset_calibrations/${calibrationID}/download_url`;

    const headers = new HttpHeaders({
      Authorization: 'jwt_token ' + this.auth.getHardwareToken(),
    });

    return this.http.get<GetCertificateURLResponse>(requestUrl, { headers: headers }).pipe(
      map(x => x.signed_url),
    );
  }
}

interface AccountsAPIResult<T> {
  data: T;
}

interface AccountSearchResult {
  collection: Account[];
  total_pages: number;
  per_page: number;
  page: number;
  total_count: number;
}

interface LoginResult {
  token: string;
  is_valid_token: boolean;
  first_name: string;
  last_name: string;
}

interface AccountDevicesResponse {
  devices: ApiDevice[];
}

interface AccountHeadsetsResponse {
  hardware_items: Headset[];
}

interface DeviceSearchResponse {
  devices: ApiDevice[];
}

export interface HeadsetSearchResponse {
  hardware: Headset;
  calibration?: HeadsetCalibration;
}

interface GetCertificateURLResponse {
  signed_url: string;
}



// WEBPACK FOOTER //
// ./src/app/account.service.ts